import { useState } from 'react'
import type { JSX } from 'react'

import clsx from 'clsx'
import {
  TriangleAlertIcon,
  Badge,
  Button,
  Card,
  DropdownMenu,
  EllipsisVerticalIcon,
  Flex,
  IconButton,
  KeyRoundIcon,
  RefreshCwIcon,
  Text,
  TrashIcon,
} from '@buffer-mono/popcorn'

import type { PersonalAccessToken } from '../../hooks'
import { ApiKeySafetyNotice } from '../../../shared/components/ApiKeySafetyNotice'
import safetyNoticeStyles from '../../../shared/components/ApiKeySafetyNotice/ApiKeySafetyNotice.module.css'
import { CopyButton } from '../../../shared/components/CopyButton'
import {
  getDaysUntilExpiration,
  isTokenExpired,
  isTokenExpiringSoon,
} from '../../../shared/utils/tokenExpiration'
import { formatDate } from '../../helpers/formatDate'
import { maskToken } from '../../helpers/maskToken'
import { OnboardingCallout } from '../OnboardingCallout'
import { ScopesDisclosure } from '../ScopesDisclosure'

import styles from './TokenInfo.module.css'

interface TokenInfoProps {
  token: PersonalAccessToken
  organizationId: string | null
  onRevoke: () => void
  onRegenerate: () => void
  showOnboarding?: boolean
  onDismissOnboarding?: () => void
}

export const TokenInfo = ({
  token,
  organizationId,
  onRevoke,
  onRegenerate,
  showOnboarding,
  onDismissOnboarding,
}: TokenInfoProps): JSX.Element => {
  const [showSafetyNotice, setShowSafetyNotice] = useState(false)
  const daysUntilExpiration = getDaysUntilExpiration(token.expiresAt)
  const isExpiringSoon = isTokenExpiringSoon(token.expiresAt)
  const isExpired = isTokenExpired(token.expiresAt)

  return (
    <Card padding="none" className={styles.keyCard}>
      <div className={styles.keyRow}>
        <div className={styles.iconBadge}>
          <KeyRoundIcon size="medium" />
        </div>

        <div className={styles.content}>
          <Flex gap="sm" align="center" wrap="wrap">
            <Text className={styles.keyName}>{token.name ?? 'API Key'}</Text>
            {!isExpired && (
              <Text className={styles.maskedToken} color="subtle">
                {maskToken(token.accessToken)}
              </Text>
            )}
            {isExpiringSoon && (
              <Badge variant="warning" size="small">
                {daysUntilExpiration}d left
              </Badge>
            )}
            {isExpired && (
              <Badge size="small" className={styles.expiredBadge}>
                <TriangleAlertIcon /> Expired
              </Badge>
            )}
          </Flex>

          <Text className={styles.keyDates}>
            Created {formatDate(token.generatedAt)} ·{' '}
            {isExpired ? 'Expired' : 'Expires'} {formatDate(token.expiresAt)}
          </Text>

          <ScopesDisclosure scopes={token.scopes} />
        </div>

        <Flex gap="xs" className={styles.actions}>
          {isExpired ? (
            <Button variant="secondary" onClick={onRegenerate}>
              <RefreshCwIcon /> Re-generate
            </Button>
          ) : (
            <CopyButton
              textToCopy={token.accessToken}
              label="Copy key"
              onCopy={() => setShowSafetyNotice(true)}
            />
          )}

          <DropdownMenu>
            <DropdownMenu.Trigger>
              <IconButton label="More actions" variant="tertiary">
                <EllipsisVerticalIcon />
              </IconButton>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content>
              {!isExpired && (
                <DropdownMenu.Item onSelect={onRegenerate}>
                  <RefreshCwIcon />
                  Regenerate
                </DropdownMenu.Item>
              )}
              <DropdownMenu.Item onSelect={onRevoke} variant="critical">
                <TrashIcon />
                Revoke
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu>
        </Flex>
      </div>

      <ApiKeySafetyNotice
        visible={showSafetyNotice}
        onDismiss={() => setShowSafetyNotice(false)}
        className={clsx(
          safetyNoticeStyles.noTopRadius,
          showOnboarding && safetyNoticeStyles.noBottomRadius,
        )}
      />

      {showOnboarding && !isExpired && onDismissOnboarding && (
        <div className={styles.onboardingSection}>
          <OnboardingCallout
            token={token.accessToken}
            organizationId={organizationId}
            onDismiss={onDismissOnboarding}
          />
        </div>
      )}
    </Card>
  )
}
